import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { setNestedObjectValues } from "formik";
import { toast } from "react-toastify";
import { useFeatureEntitlements } from "@frontegg/react";
import {
  Alert,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ConfirmDialog, Drawer, Stepper } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import EmployeeSearch from "components/admin/cards/EmployeeSearch";
import UserProfileDrawer from "components/common/users/UserProfileDrawer";
import type { Employee } from "components/common/EmployeeSearchAutoComplete";
import TermsAndPrivacyNotice from "components/common/TermsAndPrivacyNotice";
import Comments from "components/common/comments/Comments";
import CardDetailsDrawer from "components/common/cards/CardDetailsDrawer";
import CardNicknameField from "components/common/cards/CardNicknameField";
import CardPolicyForm from "components/common/cards/CardPolicyForm";
import CardPresetPicker from "components/common/cards/CardPresetPicker";
import CardSummary from "components/common/cards/CardSummary";
import { useShippingServiceTooltip } from "components/common/cards/shippingServiceTooltip";
import CardShippingSpeed from "enums/CardShippingSpeed";
import CardStatusType from "enums/CardStatusType";
import CardType from "enums/CardType";
import { ParentType } from "enums/Comments";
import Role from "enums/Role";
import { UPDATE_CARD_SHIPPING_SPEED } from "graphql/card";
import { useAdminTools } from "hooks/useAdminTools";
import { useCardData } from "hooks/useCardData";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { useComments } from "hooks/useComments";
import useGetLoggedInUser from "hooks/useGetLoggedInUser";
import { CardPolicy, resetCardPolicyForm } from "utils/cards/cardFormUtils";
import { missingCardholderDetails } from "utils/users/cardholderDetails";
import { useBusinessCardholders } from "hooks/useBusinessCardholders";
import { fullName } from "utils/fullName";

interface CardFormDrawerProps {
  open: boolean;
  /** `changed` tells the list behind whether to refetch. */
  onClose: (changed: boolean) => void;
  /** The card to edit; absent while creating one. */
  cardId?: string;
  /** Who a new card is for, when the caller already knows. */
  cardholder?: Employee;
}

/** Creating, requesting and editing a card, for every route and role. */
const CardFormDrawer: React.FC<CardFormDrawerProps> = ({
  open,
  onClose,
  cardId,
  cardholder,
}) => {
  const { t } = useTranslation();
  const isEdit = !!cardId;
  const adminTools = useAdminTools();
  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const { isEntitled: featurePhysicalCard } = useFeatureEntitlements(
    "feature_physical_card",
  );
  const { isEntitled: featureCardExpressShipping } = useFeatureEntitlements(
    "feature_card_express_shipping",
  );
  const shippingServiceTooltip = useShippingServiceTooltip();
  const { user: loggedInUser } = useGetLoggedInUser();
  const businessCardholders = useBusinessCardholders();

  const {
    formikCardMeta,
    formikCardPolicy,
    cardData,
    hasAtLeastOneLimit,
    specificLoadingStates,
    handleRequestNewCard,
    handleUpdateCard,
    handleRenameCard,
  } = useCardData({ cardId });
  const commentActions = useComments("", ParentType.CARD);
  const [updateShippingSpeed, { loading: updatingShippingSpeed }] = useMutation(
    UPDATE_CARD_SHIPPING_SPEED,
  );

  const [step, setStep] = useState(0);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [createdCardId, setCreatedCardId] = useState<string | null>(null);

  // Manager and above choose the cardholder on /admin; on /my a card is always the viewer's own.
  const searchCardholder = !isEdit && adminTools;
  const meta = formikCardMeta.values;
  const holder = meta.user;
  // A personal cardholder needs every detail the server checks; a business card only somewhere to ship.
  const holderNeedsDetails =
    !!holder &&
    (businessCardholders ? !holder.address : missingCardholderDetails(holder));
  const holderReady = !!holder && !holderNeedsDetails;
  // Only your own profile can be fixed from here; someone else's needs an admin.
  const holderIsViewer = !!holder && holder.id === loggedInUser?.id;

  const stepKeys = isEdit
    ? ["Policy", "Review"]
    : ["Details", "Policy", "Review"];
  const policyStep = isEdit ? 0 : 1;
  const summaryStep = stepKeys.length - 1;

  const external = cardData?.type === CardType.EXTERNAL;
  const policyChanged = isEdit && formikCardPolicy.dirty;
  const nicknameChanged =
    isEdit &&
    (meta.nickname ?? "") !== (formikCardMeta.initialValues.nickname ?? "");
  const canSetShippingSpeed =
    isAdminOrHigher &&
    !!featureCardExpressShipping &&
    (isEdit
      ? cardData?.type === CardType.PHYSICAL &&
        cardData?.status === CardStatusType.PENDING
      : meta.issuePhysicalCard);
  const shippingSpeed = canSetShippingSpeed ? meta.shippingSpeed : undefined;
  const speedChanged =
    isEdit &&
    canSetShippingSpeed &&
    meta.shippingSpeed !== formikCardMeta.initialValues.shippingSpeed;
  const cardChanged = policyChanged || nicknameChanged || speedChanged;
  // Under the default policy an Admin's own approval settles the change, as the form's note assumes.
  const showApprovalNote = policyChanged && !isAdminOrHigher;

  const busy =
    specificLoadingStates.loadingCardData ||
    specificLoadingStates.creatingCard ||
    specificLoadingStates.updatingCard ||
    specificLoadingStates.renamingCard ||
    updatingShippingSpeed ||
    commentActions.addingComment;

  const handleReset = () => {
    formikCardMeta.resetForm();
    resetCardPolicyForm(formikCardPolicy);
    commentActions.reset();
    setStep(0);
  };

  // Reopening restores initialValues: blank for a new card, the loaded card for an edit.
  useEffect(() => {
    if (!open) return;
    handleReset();
    setCreatedCardId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // After the reset above, and again if the viewer's profile loads or changes.
  useEffect(() => {
    if (!open || isEdit) return;
    if (cardholder) {
      formikCardMeta.setFieldValue("user", cardholder);
    } else if (!searchCardholder && loggedInUser) {
      const self: Employee = {
        id: loggedInUser.id,
        firstName: loggedInUser.firstName,
        lastName: loggedInUser.lastName,
        email: loggedInUser.email,
        hasPhysicalCard: loggedInUser.hasPhysicalCard,
        address: loggedInUser.address,
        phone: loggedInUser.phoneNumber,
        birthdate: loggedInUser.birthdate,
      };
      formikCardMeta.setFieldValue("user", self);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, cardholder, searchCardholder, loggedInUser]);

  const close = (changed: boolean) => {
    setConfirmCancelOpen(false);
    onClose(changed);
  };

  const handleCloseAttempt = () => {
    if (busy) return;
    const unsaved = isEdit ? cardChanged || commentActions.hasDraft : step > 0;
    if (unsaved) {
      setConfirmCancelOpen(true);
      return;
    }
    close(false);
  };

  const handleNext = async () => {
    if (step === policyStep) {
      const errors = await formikCardPolicy.validateForm();
      if (Object.keys(errors).length !== 0) {
        formikCardPolicy.setTouched(setNestedObjectValues(errors, true));
        return;
      }
    }
    setStep((prev) => Math.min(summaryStep, prev + 1));
  };

  const handleSubmit = async () => {
    try {
      if (isEdit) {
        const updated = policyChanged ? await handleUpdateCard() : undefined;
        const waiting = !!updated?.data?.requestUpdateCard?.pendingRequest;
        if (nicknameChanged) await handleRenameCard();
        if (speedChanged) {
          await updateShippingSpeed({
            variables: { id: cardId, shippingSpeed: meta.shippingSpeed },
          });
        }
        if (commentActions.hasDraft) await commentActions.submit(cardId);
        toast.success(
          waiting
            ? t("Change request submitted!")
            : cardChanged
              ? t("Card updated")
              : t("Comment added"),
        );
        close(true);
        return;
      }
      const result = await handleRequestNewCard(shippingSpeed);
      const created = result?.data?.requestNewCard;
      if (created?.id && commentActions.hasDraft) {
        await commentActions.submit(created.id);
      }
      toast.success(
        created?.status === CardStatusType.PENDING
          ? t("Card request submitted!")
          : t("Card created!"),
      );
      if (created?.id) setCreatedCardId(created.id);
      else close(true);
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : t("Please try again!"),
      );
    }
  };

  // A new card needs a limit; an edit that leaves the policy alone may only rename, and an
  // external card's limits are read-only, so they cannot be what holds a change back.
  const nextDisabled =
    (step === 0 && !isEdit && !holderReady) ||
    (step === policyStep &&
      (!isEdit || policyChanged) &&
      !external &&
      !hasAtLeastOneLimit(formikCardPolicy.values));
  const submitDisabled = isEdit && !cardChanged && !commentActions.hasDraft;

  const nicknameField = (
    <CardNicknameField
      nickname={meta.nickname}
      onNicknameChange={formikCardMeta.handleChange}
      onNicknameClear={() => formikCardMeta.setFieldValue("nickname", "")}
    />
  );

  const shippingSpeedField = canSetShippingSpeed && (
    <Stack gap="var(--space-3)">
      <Inline gap="var(--space-2)" wrap={false}>
        <Typography
          variant="label"
          component="span"
          sx={{ color: "var(--color-text-secondary)" }}
        >
          {t("Shipping Speed")}
        </Typography>
        <Tooltip title={shippingServiceTooltip}>
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Inline>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={meta.shippingSpeed}
        onChange={(_event, value) => {
          if (value !== null)
            formikCardMeta.setFieldValue("shippingSpeed", value);
        }}
      >
        <ToggleButton value={CardShippingSpeed.Standard}>
          {t("Standard")}
        </ToggleButton>
        <ToggleButton value={CardShippingSpeed.Express}>
          {t("Express")}
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );

  const footer = (
    <Inline justify="between" align="center" sx={{ width: "100%" }}>
      <Inline gap="var(--space-3)">
        <Button variant="default-outlined" onClick={handleCloseAttempt}>
          {t("Cancel")}
        </Button>
        {step > 0 && (
          <Button
            variant="default-outlined"
            onClick={() => setStep((prev) => Math.max(0, prev - 1))}
          >
            {t("Back")}
          </Button>
        )}
      </Inline>
      {step < summaryStep ? (
        <Button
          variant="primary-filled"
          onClick={handleNext}
          disabled={nextDisabled}
        >
          {t("Next")}
        </Button>
      ) : (
        <Button
          variant="primary-filled"
          onClick={handleSubmit}
          disabled={submitDisabled}
        >
          {t("Submit")}
        </Button>
      )}
    </Inline>
  );

  return (
    <>
      <Drawer
        open={open && !createdCardId}
        onClose={handleCloseAttempt}
        onAfterClose={handleReset}
        loading={busy}
        title={
          isEdit
            ? t("Edit Card")
            : adminTools
              ? t("Create Card")
              : t("Request New Card")
        }
        footer={footer}
      >
        <Stack gap="var(--space-6)" sx={{ marginTop: "var(--space-2)" }}>
          <Stepper
            activeStep={step}
            steps={stepKeys.map((key) => ({ id: key, label: t(key) }))}
          />

          {!isEdit && step === 0 && (
            <Stack gap="var(--space-5)">
              {searchCardholder ? (
                <EmployeeSearch
                  description={t("Specify who will be using this new card")}
                  employee={holder}
                  onSelectEmployee={(employee) =>
                    formikCardMeta.setFieldValue("user", employee)
                  }
                />
              ) : (
                holderNeedsDetails && (
                  <Alert
                    severity="warning"
                    role="alert"
                    action={
                      holderIsViewer ? (
                        <Button
                          variant="primary-outlined"
                          onClick={() => setProfileOpen(true)}
                        >
                          {t("Add Information")}
                        </Button>
                      ) : undefined
                    }
                  >
                    {holderIsViewer
                      ? t("YourRequiredInfo")
                      : t("UserRequiredInfo")}
                  </Alert>
                )
              )}
              {nicknameField}
              {holderReady && <CardPresetPicker formik={formikCardPolicy} />}
              {featurePhysicalCard && (
                <Stack gap="var(--space-3)">
                  <Typography
                    variant="label"
                    component="span"
                    sx={{ color: "var(--color-text-secondary)" }}
                  >
                    {t("Card Options")}
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={meta.issuePhysicalCard ? "physical" : "virtual"}
                    onChange={(_event, value) => {
                      if (value !== null) {
                        formikCardMeta.setFieldValue(
                          "issuePhysicalCard",
                          value === "physical",
                        );
                      }
                    }}
                  >
                    <ToggleButton value="virtual">
                      {t("Virtual Card")}
                    </ToggleButton>
                    <ToggleButton value="physical">
                      {t("Physical Card")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              )}
              {shippingSpeedField}
              <Typography
                variant="caption"
                sx={{ color: "var(--color-text-tertiary)" }}
              >
                {t("CardIssuerDisclosure")}{" "}
                <a
                  href="https://brandfetch.com/developers/logo-api"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline" }}
                >
                  {t("BrandFetchFooter")}
                </a>
              </Typography>
            </Stack>
          )}

          {step === policyStep && (
            <Stack gap="var(--space-5)">
              {isEdit && nicknameField}
              {isEdit && shippingSpeedField}
              <CardPolicyForm
                formik={formikCardPolicy}
                card={isEdit ? cardData : undefined}
              />
            </Stack>
          )}

          {step === summaryStep && (
            <Stack gap="var(--space-5)">
              {showApprovalNote && (
                <Typography
                  variant="body2"
                  sx={{ color: "var(--color-text-secondary)" }}
                >
                  {t("ChangeRequestApprovalNote")}
                </Typography>
              )}
              {isEdit && nicknameChanged && (
                <Typography
                  variant="body2"
                  sx={{ color: "var(--color-text-secondary)" }}
                >
                  {t(
                    "Your new nickname saves straight away and needs no approval",
                  )}
                </Typography>
              )}
              <CardSummary
                nickname={meta.nickname}
                previousNickname={
                  isEdit ? formikCardMeta.initialValues.nickname : undefined
                }
                cardholderName={
                  searchCardholder && holder ? fullName(holder) : undefined
                }
                cardType={
                  isEdit
                    ? cardData?.type
                    : meta.issuePhysicalCard
                      ? CardType.PHYSICAL
                      : CardType.VIRTUAL
                }
                shippingSpeed={shippingSpeed}
                policy={formikCardPolicy.values}
                previousPolicy={
                  isEdit
                    ? (formikCardPolicy.initialValues as CardPolicy)
                    : undefined
                }
              />
              <Stack gap="var(--space-2)">
                <Typography variant="body1">{t("Comments")}</Typography>
                <Comments
                  commentActions={commentActions}
                  parentId=""
                  parentType={ParentType.CARD}
                  rows={4}
                  placeholder={t("AddCommentWithMention")}
                />
              </Stack>
              {!isEdit && <TermsAndPrivacyNotice />}
            </Stack>
          )}
        </Stack>
      </Drawer>

      <ConfirmDialog
        open={confirmCancelOpen}
        onCancel={() => setConfirmCancelOpen(false)}
        onConfirm={() => close(false)}
        title={isEdit ? t("Discard changes") : t("Cancel card set up")}
        message={t(
          "All data entered will be lost are you sure you want to continue?",
        )}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
      />

      {profileOpen && (
        <UserProfileDrawer
          open={profileOpen}
          edit
          selectedUserId={loggedInUser?.id}
          handleCloseDrawer={() => setProfileOpen(false)}
        />
      )}

      {createdCardId && (
        <CardDetailsDrawer
          open
          cardId={createdCardId}
          onClose={() => {
            setCreatedCardId(null);
            onClose(true);
          }}
        />
      )}
    </>
  );
};

export default CardFormDrawer;
